import type { ReactNode } from "react";
import { cn } from "./cn";

export interface TimelineEntry {
  id: string;
  /** What happened, as a sentence. */
  title: ReactNode;
  /** The reason, the comment, whatever needs saying underneath. */
  detail?: ReactNode;
  meta?: ReactNode;
  /** Draws attention to the entries that changed something material. */
  emphasis?: boolean;
}

/**
 * A vertical run of things that happened, newest first.
 *
 * <p>The connecting rule is drawn behind the markers rather than between them, so an entry whose
 * detail wraps to four lines does not leave a gap in the line. It stops short of the last marker:
 * a line continuing past the final entry suggests there is more below, and there is not.
 */
export function Timeline({ entries }: { entries: TimelineEntry[] }) {
  if (entries.length === 0) {
    return <p className="py-6 text-center text-sm text-ink-500">Nothing recorded yet.</p>;
  }

  return (
    <ol className="relative space-y-4">
      {entries.map((entry, index) => (
        <li key={entry.id} className="relative flex gap-3">
          <div className="relative flex w-2 shrink-0 justify-center">
            <span
              className={cn(
                "relative z-10 mt-1.5 h-2 w-2 shrink-0 rounded-full",
                entry.emphasis ? "bg-brand-500 ring-4 ring-brand-50" : "bg-ink-300",
              )}
              aria-hidden
            />
            {index < entries.length - 1 && (
              <span
                className="absolute left-1/2 top-3 h-[calc(100%+1rem)] w-px -translate-x-1/2 bg-ink-200"
                aria-hidden
              />
            )}
          </div>
          <div className="min-w-0 flex-1 pb-0.5">
            <div className="text-sm text-ink-800">{entry.title}</div>
            {entry.detail != null && (
              <div className="mt-1 rounded-md border-l-2 border-ink-200 bg-ink-50 px-2 py-1 text-xs text-ink-700">
                {entry.detail}
              </div>
            )}
            {entry.meta != null && (
              <div className="mt-0.5 text-xs text-ink-400">{entry.meta}</div>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
